<template>
  <div class="sm-update-billing">
    <p>
      <a 
        href="#" 
        class="sm-update-billing__link"
        @click.prevent="$emit('back')"
      >Back to Plans & Billing</a>
    </p>

    <template v-if="(creditCards && creditCards.length > 0) || provider.client_level == 3">
      <h4 class="sm-update-billing__title sm-h4">Credit and Debit cards</h4>

      <transition-group 
        name="flip-list" 
        tag="ul" 
        class="sm-update-billing__card-list"
      >
        <li v-for="card in creditCards" :key="card.id" class="sm-update-billing__card-item">
          <credit-card-index
            :card="card"
            :canEdit="provider.payment_gateway == 'stripe'"
            :canDelete="false"
            @default-card="setDefaultCard"
            @delete-card="processDeleteCard"
            @edit-card="editCard"
          ></credit-card-index>
        </li>
        <li :key="`add-new`" style="transition: none !important;">
          <button type="button" class="sm-update-billing__new" @click="addNewCard">
            <span>Add card</span>
          </button>
        </li>
      </transition-group>
    </template>
    <div 
      v-else-if="cardsReceived"
      class="sm-update-billing__empty"
    >
      <h5>You have no credit or debit card yet</h5>
      <p>Your account was activated by administrator.</p>
      <p>Added cards displayed here</p>
      <button 
        type="button" 
        class="sm-update-billing__new" 
        @click="addNewCard"
      >
        <span>Add card</span>
      </button>
    </div>

    <bank-account-wrap
      v-if="provider.client_level == 3 && bankAccount"
      :bankAccount="bankAccount"
      :canDelete="false"
      @add="addBank"
      @default="setBankDefault"
      @delete="deleteBank"
    />

    <popup-block
      v-if="popup.isOpen"
      :size="popup.size"
      :open="popup.isOpen"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent" v-if="popup.state === 'edit'">
        <form class="sm-plans__billing-info"
          @submit.prevent="handlerSubmitEdit"
        >
          <h4>Edit card</h4>

          <div class="sm-plans__billing-edit sm-credit-card">
            <p 
              class="sm-plans__billing-edit-number"
              
            >
              <span 
                v-if="toEditCard.brand"
                :class="`card-brand card-brand-${toEditCard.brand.toLowerCase()}`"
              ></span>
              <span class="sm-h5">{{ toEditCard.number }}</span>
            </p>

            <p class="sm-red"
              v-if="toEditCard.error"
            >{{
               toEditCard.error
            }}</p>

            <div class="sm-plans__billing-edit-exp">
              <input-text
                v-model="toEditCard.expiration.month"
                :value="toEditCard.expiration.month"
                :autocomplete="`cc-exp-month`"
                :label="'New expiration date'"
                :placeholder="`MM`"
              />

              <input-text
                v-model="toEditCard.expiration.year"
                :value="toEditCard.expiration.year"
                :autocomplete="`cc-exp-year`"
                :label="' '"
                :placeholder="`YYYY`"
              />
            </div>
          </div>

          <p class="sm-plans__billing-info-footer sm-txt-center">
            <btn-block type="submit" :text="'Save changes'" :class="{'disabled': disabledFormEdit }" />
          </p>
        </form>
      </template>
      <template slot="popupContent" v-if="popup.state === 'new'">
        <new-card
          @create="createCard"
        />
      </template>
      <template slot="popupContent" v-if="popup.state === 'delete'">
        <confirm-block
          v-if="creditCards.length > 1" 
          :text="`Your card ×× ${toDeleteCard.last4} will be  permanently deleted`"
          :confirmText="'Delete'"
          :confirmType="'warning'"
          @confirm="handlerDelete"
          @cancel="popup.isOpen = false"
        />
        <div
          v-else
          class="sm-plans__billing-delete"
        >
          <h4 class="sm-h4">You need to have at least one credit card attached.</h4>
          <btn-block 
            type="button" 
            class="sm-plans__billing-delete-ok"
            :text="'Ok'"
            :class="`sm-btn--small`"
            @click.once="popup.isOpen = false"
          />
        </div>
      </template>
    </popup-block>
    
    <sm-spinner :show="showSpinner"></sm-spinner>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import InputText from '@/components/InputText';
import CreditCardIndex from '@/components/credit-card/CreditCardIndex';
import Popup from '@/components/Popup';
import Spinner from '@/components/Spinner';
import AddNewCard from '@/components/credit-card/AddNewCard';
import Confirm from '@/components/Confirm';
import {FormatYear} from '@/utils/general-data';
import {ParseErrorText} from '@/utils/errors';

import Stripe from '@/mixins/Stripe';
import BankAccountWrap from '@/components/account/BankAccountWrap';

export default {
  name: 'update-billing-info',
  components: {
    'popup-block': Popup,
    'input-text': InputText,
    'credit-card-index': CreditCardIndex,
    'sm-spinner': Spinner,
    'new-card': AddNewCard,
    'confirm-block': Confirm,
    'bank-account-wrap': BankAccountWrap,
  },
  mixins: [Stripe],
  data() {
    return {
      popup: {
        isOpen: false,
        size: 'default',
        state: ''
      },

      seletedToEdit: {},

      toEditCard: {
        number: '',
        brand: '',
        error: '',
        expiration: {
          month: '',
          year: '',
        }
      },

      toDeleteCard: {},

      card: null,
      address: null,
      showSpinner: false,
      cardsReceived: false,
    };
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      creditCards: 'account/creditCards',
      bankAccount: 'billing/bankAccount'
    }),
    disabledFormNew() {
      return (
        Object.values(this.address).some(item => !item) ||
        Object.values(this.card).some(item => !item)
      );
    },
    disabledFormEdit() {
      return (
        this.toEditCard.expiration.month.toString() === this.seletedToEdit.exp_month.toString() &&
        this.toEditCard.expiration.year.toString() === this.seletedToEdit.exp_year.toString()
      );
    }
  },
  created() {
    this.getBillingInfo();
  },
  methods: {
    ...mapActions({
      addCard: 'account/addCard',
      getAllCards: 'account/getAllCards',
      addBankAccount: 'billing/addBankAccount',
      setBankAccountDefault: 'billing/setBankAccountDefault',
      deleteBankAccount: 'billing/deleteBankAccount',
      getBankInfo: 'billing/getBankInfo',
      setAsDefault: 'account/setAsDefaultCard',
      updateCardExp: 'account/updateCardExp',
      deleteCard: 'account/deleteCard',
      showGlobalNotification: 'showGlobalNotification',
      connectToSpreedly: 'billing/connectToSpreedly'
    }),
    formatYear: FormatYear,
    getBillingInfo(callback) {
      this.showSpinner = true;

      this.getAllCards()
        .then(res => {
          if (this.provider.client_level == 3) {
            this.getBankInfo()
              .then(res => {
                this.showSpinner = false;

                if (callback)
                  callback();
              });
          } else {
            this.showSpinner = false;

            if (callback)
              callback();
          }
        });
    },
    stripeTokenHandler(status, result) {
      if (result.error) {
        this.popup.isOpen = true;
        this.popup.size = 'large';
        this.popup.state = 'new';

        this.errors.add({
          field: 'credit_card',
          msg: result.error.message,
        });

        this.showSpinner = false;

        // Inform the user if there was an error.
        // var errorElement = document.getElementById('card-errors');
        // errorElement.textContent = result.error.message;
      } else {
        this.submitToDatabase(result);
      }
    },
    submitToDatabase(result) {
      this.showSpinner = true;

      const data = {
        stripe_token: result.id,
        brand: result.card.brand
      };

      this.addCard(data)
        .then(res => {
          this.createCardCallback();
        })
        .catch((e) => {
          this.showSpinner = false;
          this.popup.isOpen = false;

          if (e.response) {
            if (e.response.data && e.response.data.errors && e.response.data.errors.base)
              this.showGlobalNotification({
                type: 'error',
                value: ['Card handling error', `Please provide following message to our support: '${e.response.data.errors.base}'`]
              });
            else
              this.showGlobalNotification({
                type: 'error',
                value: ['Card handling error', `Card is not created`]
              });
          }
        });
    },
    addNewCard() {
      this.popup.isOpen = true;
      this.popup.size = 'large';
      this.popup.state = 'new';
    },
    setDefaultCard(id) {
      this.showSpinner = true;
      this.setAsDefault(id)
        .then(res => {
          this.getBillingInfo(res => {
            this.showSpinner = false;
          });
        });
    },
    processDeleteCard(id) {
      this.popup.state = 'delete';
      this.popup.size = 'default';
      this.popup.isOpen = true;

      this.toDeleteCard = this.creditCards.find(item => item.id === id);
    },
    handlerDelete() {
      this.showSpinner = true;
      this.popup.isOpen = false;
      this.deleteCard(this.toDeleteCard.id)
        .then(() => {
          this.showSpinner = false;
        });
    },
    editCard(id) {
      if (this.provider.payment_gateway != 'stripe')
        return;

      const card = this.creditCards.find(item => item.id === id);
      this.seletedToEdit = card;

      this.toEditCard = {
        number: `xxxx xxxx xxxx ${card.last4}`,
        brand: card.brand,
        expiration: {
          month: card.exp_month.toString(),
          year: card.exp_year.toString(),
        },
      };

      this.popup.isOpen = true;
      this.popup.size = 'default';
      this.popup.state = 'edit';
    },
    handlerSubmitEdit() {
      const data = { 
        month: this.toEditCard.expiration.month,
        year: this.toEditCard.expiration.year,
      };

      this.showSpinner = true;

      this.updateCardExp({id: this.seletedToEdit.id, data})
        .then(
          () => {
            this.popup.isOpen = false;
            this.showSpinner = false;
          }
        )
        .catch(e => {
          const error = e.response;

          if (error && error.status === 422 && error.data && error.data.errors) {
            if (error.data.errors.base)
              this.toEditCard.error = error.data.errors.base[0];
            else
              this.toEditCard.error = error.data.errors[0];

            this.showSpinner = false;
            this.$forceUpdate(); 
          }
        });
    },
    createCard(card, address) {
      this.card = card;
      this.address = address;

      this.showSpinner = true;
      if (this.provider.payment_gateway == 'stripe') { // Stripe
        this.handlerSubmitNew();
      } else { // Spreedly or other payment gateway
        let nameSplit = this.address.name.split(' ');

        this.connectToSpreedly({
          number: this.card.number,
          year: this.formatYear(this.card.expiration.year),
          month: this.card.expiration.month,
          verification_value: this.card.cvc,
          first_name: nameSplit[0],
          last_name: nameSplit.length > 1 ? nameSplit[1] : ' ',
          address1: this.address.street,
          address2: null,
          city: this.address.city,
          zip: this.address.zip,
          state: this.address.state ? this.address.state.code : null,
          country: this.address.country ? this.address.country.code :'US'
        })
          .then(res => {
            this.getBillingInfo(this.createCardCallback);
          })
          .catch(e => {
            console.log('Spreedly error', e, e.response);
            let err = e.response;

            if (err && err.data && err.data.errors) {
              let errorMsg = ParseErrorText(err.data.errors);

              if (errorMsg) {
                this.errors.add({
                  field: 'credit_card',
                  msg: errorMsg,
                });

                this.popup.isOpen = true;
                this.popup.size = 'large';
                this.popup.state = 'new';
              }
            }

            this.showSpinner = false;
          });
      }
    },
    createCardCallback() {
      this.popup.isOpen = false;
      this.showSpinner = false;

      this.$nextTick(() => {
        this.card = null;
        this.address = null;
      });
    },
    addBank(data) {
      console.log('add', data);

      let accID = data.account_id;

      if (!accID)
        accID = data.account.id;

      if (!accID && data.accounts.length)
        accID = data.accounts[0].id; // TEMP?

      if (!accID)
        return; // handling

      this.showSpinner = true;
      
      this.addBankAccount({
        plaid_public_token: data.public_token, 
        plaid_account_id: accID
      })
        .then(res => {
          this.getBillingInfo();
        })
    },
    setBankDefault() {
      this.showSpinner = true;

      this.setBankAccountDefault()
        .then(res => {
          this.getBillingInfo();
        })
    },
    deleteBank() {
      this.showSpinner = true;
      
      this.deleteBankAccount()
        .then(res => {
          this.getBillingInfo();
        })
    }
  },
  watch: {
    'toEditCard.expiration.month'(val) {
      this.toEditCard.error = '';
      const value = parseInt(val);

      if (value > 0 && value <= 12) return false;

      if (value <= 0) this.toEditCard.expiration.month = `1`;
      if (value > 12) this.toEditCard.expiration.month = `12`;
    },
    'toEditCard.expiration.year'(val) {
      this.toEditCard.error = '';  
    },
    creditCards(val) {
      if (val && !this.cardsReceived)
        this.cardsReceived = true;
    }
  }
};
</script>

<style lang="scss">
@import '@/assets/scss/general/mixins.scss';
@import '@/assets/scss/framework/scss-variables.scss';
@import '@/assets/scss/pages/customer/account/components/_update-billing.scss';

.flip-list-move {
  transition: transform 0.5s;
}
</style>